import mustache from "mustache";
import { AnalisisNutrienteResponse } from "../../../../modulos/fertilizacion/response/AnalisisNutrienteResponse";
import { AnalisisParametroResponse } from "../../../../modulos/fertilizacion/response/AnalisisParametroResponse";
import { AnalisisRegistroResponse } from "../../../../modulos/fertilizacion/response/AnalisisRegistroResponse";
import { ClienteFincaLoteResponse } from "../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import {
  estadoDisplay,
  nutrienteSimbolo,
  parametroNombre,
  severidadLabel,
  unidadVisible,
} from "../../../../utils/fertilizacionUtils";
import { _2d } from "../../../../utils/numberUtils";
import { IRender } from "../../../definitions/IRender";
import template from "./fertilizacionDiagnosticoCardTemplate.html";

mustache.parse(template);

/** Nutriente que es enmienda orgánica: no va a la tabla, va al aviso verde. */
const TIPO_ORGANICO = "organic_amendment";

/** `tier` del último nivel que cuenta como enmienda (0=estructural, 1=encalado);
 *  de 2 en adelante son nutrientes. */
const TIER_ENMIENDA_MAX = 1;

/** `tier` de los macronutrientes; por encima, secundarios y micros. */
const TIER_MACROS = 2;

/** Una card del diagnóstico: un grupo de lotes con plan compartido (modo
 *  Nitrofert) o un lote suelto (modo Sextante). */
export type DiagnosticoCard = {
  /** Registro que aporta el plan (el de referencia, si es un grupo). */
  registro: AnalisisRegistroResponse;
  /** Lotes que cubre la card. */
  lotes: ClienteFincaLoteResponse[];
  /** Etiqueta de agrupación ("Lote único" / "3 lotes agrupados"). */
  tag: string;
  /** `true` si la card representa un grupo real (cambia el color del tag). */
  esGrupo: boolean;
  /** Área total de la card en hectáreas. */
  areaHa: number;
  /** Id del elemento en el DOM. */
  cardId: string;
  /** Si arranca desplegada. */
  open: boolean;
};

/** Una fila de la tabla de la card: un parámetro con la dosis que le
 *  corresponde (o el nutriente que ya lo cubre). */
type FilaVm = {
  parametro: string;
  valor: string;
  rango: string;
  estadoLabel: string;
  estadoBadge: string;
  nutriente: string;
  dosis: string;
  /** `true` si otro nutriente ya lo corrige (fila sin dosis propia). */
  cubierto: boolean;
  /** `true` si el nutriente asociado tiene su absorción comprometida. */
  bloqueado: boolean;
};

/** Card de diagnóstico (solo `render()`: el plegado lo resuelve `<details>`,
 *  sin listeners). Arma la tabla **por parámetro**, recorriendo los nutrientes
 *  ordenados por `tier` y emitiendo, por cada uno, la fila de su
 *  `sourceParameter` primario (con la dosis) más una fila "cubierto por…" por
 *  cada driver adicional de `sourceParameters`. */
export class FertilizacionDiagnosticoCardRender implements IRender {
  /** @param _card Datos ya agrupados de la card. */
  constructor(private readonly _card: DiagnosticoCard) {}

  render(): string {
    const registro = this._card.registro;
    const filas = this._buildFilas();
    const bloqueadas = filas.filter((fila) => fila.bloqueado);
    const libres = filas.filter((fila) => !fila.bloqueado);

    return mustache.render(template, {
      cardId: this._card.cardId,
      open: this._card.open,
      tono: this._tono(),
      label: this._label(),
      tag: this._card.tag,
      tagBadge: this._card.esGrupo ? "sx-badge-info" : "sx-badge-neutral",
      avisoCultivo: this._avisoCultivo(),
      lotes: this._card.lotes.map((lote) => ({
        nombre: lote.nombreLote,
        meta: `${lote.cultivo?.nombre ?? "Sin especificar"} · ${_2d(lote.areaHectareas)} ha`,
      })),
      secciones: this._buildSecciones(libres, bloqueadas),
      moTexto: this._moTexto(),
      antagonismo: this._antagonismo(),
      totalParametros: registro.parametros.length,
      todos: registro.parametros.map((parametro) => this._buildTodos(parametro)),
      footer: this._buildFooter(filas, bloqueadas.length),
    });
  }

  /** Color de la card según la severidad agregada del lote. */
  private _tono(): string {
    switch (this._card.registro.severityLevel) {
      case "critical":
        return "red";
      case "deficient":
        return "amber";
      default:
        return "teal";
    }
  }

  /** Etiqueta del header: **categoría del plan** + motivos, como el diseño
   *  original ("Enmienda estructural + Macronutrientes", "Micronutrientes — Mg
   *  y B bajos"). La categoría sale de los `tier` presentes:
   *  - hay algún nutriente con la absorción comprometida → "Enmienda crítica"
   *    a secas (lo demás está pendiente de esa corrección, no es el titular);
   *  - si no, "Enmienda estructural" cuando hay tier 0-1, más
   *    "Macronutrientes" cuando hay tier 2;
   *  - y "Micronutrientes" solo cuando no hay ni enmienda ni macros.
   *  Sin nutrientes químicos, cae a la severidad ("Mantenimiento"). */
  private _label(): string {
    const registro = this._card.registro;
    const quimicos = registro.nutrientes.filter(
      (nutriente) => nutriente.nutrientType !== TIPO_ORGANICO,
    );
    const partes: string[] = [];

    if (quimicos.some((nutriente) => nutriente.efficiencyWarning != null)) {
      partes.push("Enmienda crítica");
    } else {
      if (quimicos.some((nutriente) => nutriente.tier <= TIER_ENMIENDA_MAX)) {
        partes.push("Enmienda estructural");
      }
      if (quimicos.some((nutriente) => nutriente.tier === TIER_MACROS)) {
        partes.push("Macronutrientes");
      } else if (
        partes.length === 0 &&
        quimicos.some((nutriente) => nutriente.tier > TIER_MACROS)
      ) {
        partes.push("Micronutrientes");
      }
    }

    const base = partes.join(" + ") || severidadLabel(registro.severityLevel);
    const motivos = registro.severityReasons.join(" + ");
    return motivos ? `${base} — ${motivos}` : base;
  }

  /** Aviso cuando el cultivo del registro no está resuelto: sin cultivo no hay
   *  rangos de referencia, así que todos los parámetros salen sin clasificar.
   *  Es informativo — todavía no hay endpoint para asignar el cultivo. */
  private _avisoCultivo(): string {
    if (this._card.registro.cropResolutionStatus === "resolved") return "";
    return "Cultivo sin confirmar: no hay rangos de referencia para clasificar los parámetros de este lote.";
  }

  /** Filas de la tabla, recorriendo los nutrientes químicos por `tier`. Un
   *  parámetro aparece una sola vez: gana la fila con dosis propia. */
  private _buildFilas(): FilaVm[] {
    const parametroPorNombre = new Map(
      this._card.registro.parametros.map((parametro) => [
        parametro.nombre,
        parametro,
      ]),
    );
    const nutrientes = this._card.registro.nutrientes
      .filter((nutriente) => nutriente.nutrientType !== TIPO_ORGANICO)
      .slice()
      .sort((a, b) => a.tier - b.tier);

    const filas: FilaVm[] = [];
    const vistos = new Set<string>();

    // 1ª pasada: la fila con dosis de cada nutriente (su driver primario).
    for (const nutriente of nutrientes) {
      const clave = nutriente.sourceParameter ?? nutriente.sourceParameters[0];
      if (!clave || vistos.has(clave)) continue;
      vistos.add(clave);
      filas.push(
        this._buildFila(
          clave,
          parametroPorNombre.get(clave),
          nutriente,
          `${_2d(nutriente.kgHa)} kg/ha`,
          false,
        ),
      );
    }

    // 2ª pasada: los drivers extra de una dosis fusionada quedan "cubiertos".
    for (const nutriente of nutrientes) {
      for (const clave of nutriente.sourceParameters) {
        if (vistos.has(clave)) continue;
        vistos.add(clave);
        filas.push(
          this._buildFila(
            clave,
            parametroPorNombre.get(clave),
            nutriente,
            `cubierto por ${nutrienteSimbolo(nutriente.nutrient)}`,
            true,
          ),
        );
      }
    }

    return filas;
  }

  /** Arma una fila cruzando el parámetro (valor/rango/estado) con el nutriente
   *  que lo atiende (símbolo/dosis). */
  private _buildFila(
    clave: string,
    parametro: AnalisisParametroResponse | undefined,
    nutriente: AnalisisNutrienteResponse,
    dosis: string,
    cubierto: boolean,
  ): FilaVm {
    const estado = estadoDisplay(parametro?.estado ?? null);
    return {
      parametro: parametroNombre(clave),
      valor: parametro ? this._formatValor(parametro) : "—",
      rango: parametro ? this._formatRango(parametro) : "—",
      estadoLabel: estado.label,
      estadoBadge: estado.badge,
      nutriente: nutrienteSimbolo(nutriente.nutrient),
      dosis,
      cubierto,
      bloqueado: nutriente.efficiencyWarning != null,
    };
  }

  /** Fila de la tabla completa ("ver todos los parámetros"): sin nutriente. */
  private _buildTodos(parametro: AnalisisParametroResponse): {
    parametro: string;
    valor: string;
    rango: string;
    estadoLabel: string;
    estadoBadge: string;
  } {
    const estado = estadoDisplay(parametro.estado);
    return {
      parametro: parametroNombre(parametro.nombre),
      valor: this._formatValor(parametro),
      rango: this._formatRango(parametro),
      estadoLabel: estado.label,
      estadoBadge: estado.badge,
    };
  }

  /** Valor + unidad (o el texto, si el parámetro es categórico). */
  private _formatValor(parametro: AnalisisParametroResponse): string {
    if (parametro.isCategorical) return parametro.textValue ?? "—";
    if (parametro.valor == null) return "—";
    const unidad = unidadVisible(parametro.unidad)
      ? ` ${parametro.unidad}`
      : "";
    return `${_2d(parametro.valor)}${unidad}`;
  }

  /** `rangoTexto` + unidad — el server lo manda formateado pero sin unidad. */
  private _formatRango(parametro: AnalisisParametroResponse): string {
    if (!parametro.rangoTexto) return "—";
    const unidad = unidadVisible(parametro.unidad)
      ? ` ${parametro.unidad}`
      : "";
    return `${parametro.rangoTexto}${unidad}`;
  }

  /** Una sola tabla, salvo que haya nutrientes con absorción comprometida: ahí
   *  se parte en "aplicar primero" y "pendiente", como el diseño original (el
   *  corte lo marca el bloqueo, no el `tier`). */
  private _buildSecciones(
    libres: FilaVm[],
    bloqueadas: FilaVm[],
  ): { label: string; tono: string; blocked: boolean; rows: FilaVm[] }[] {
    if (bloqueadas.length === 0 || libres.length === 0) {
      const rows = libres.length > 0 ? libres : bloqueadas;
      if (rows.length === 0) return [];
      return [
        {
          label: "Correcciones requeridas",
          tono: "",
          blocked: false,
          rows,
        },
      ];
    }
    return [
      {
        label: "Enmiendas — aplicar primero",
        tono: "red",
        blocked: false,
        rows: libres,
      },
      {
        label: "Nutrientes — pendientes de la corrección base del suelo",
        tono: "pending",
        blocked: true,
        rows: bloqueadas,
      },
    ];
  }

  /** Aviso de materia orgánica / enmienda orgánica (no bloquea nada). */
  private _moTexto(): string {
    const organico = this._card.registro.nutrientes.find(
      (nutriente) => nutriente.nutrientType === TIPO_ORGANICO,
    );
    if (!organico) return "";
    const dosis = `${_2d(organico.kgHa)} kg/ha`;
    return organico.note ? `${organico.note} (${dosis})` : `Aplicar ${dosis}.`;
  }

  /** Alerta de antagonismo: el primer `efficiencyWarning` con su absorción
   *  esperada. */
  private _antagonismo(): string {
    const nutriente = this._card.registro.nutrientes.find(
      (item) => item.efficiencyWarning != null,
    );
    if (!nutriente) return "";
    const pct = nutriente.estimatedEfficiencyPct;
    const absorcion =
      pct != null ? ` Absorción esperada sin corregir: ${pct}%.` : "";
    return `${nutriente.efficiencyWarning}${absorcion}`;
  }

  /** Footer de 3 stats: área, cuántos nutrientes hay que corregir y — si hay
   *  bloqueados — cuántos, o si no la química requerida. */
  private _buildFooter(
    filas: FilaVm[],
    bloqueadas: number,
  ): { val: string; lbl: string }[] {
    const quimicos = this._card.registro.nutrientes.filter(
      (nutriente) => nutriente.nutrientType !== TIPO_ORGANICO,
    );
    const simbolos = [
      ...new Set(quimicos.map((nutriente) => nutrienteSimbolo(nutriente.nutrient))),
    ].join(" · ");

    return [
      { val: `${_2d(this._card.areaHa)} ha`, lbl: "Área" },
      { val: `${filas.length}`, lbl: "Parámetros a corregir" },
      bloqueadas > 0
        ? { val: `${bloqueadas}`, lbl: "Bloqueados" }
        : { val: simbolos || "—", lbl: "Química requerida" },
    ];
  }
}
